import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { useState, useCallback, useMemo, memo, useEffect } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    Grid,
    Typography,
    Box,
    Chip,
    Paper,
    TextField,
    CircularProgress,
} from "@mui/material";
import { MapGroup } from "./SpotsComponents/MapGroup";



// Component to automatically zoom to the area with most spots
const MapFocusEffect = memo(({ spots }) => {
    const map = useMap();
    
    useEffect(() => {
        if (!spots || spots.length === 0) return;
        
        let maxDensitySpot = spots[0];
        let maxDensity = spots[0].density || 0;
        
        for (let i = 1; i < spots.length; i++) {
            if (spots[i].density > maxDensity) {
                maxDensity = spots[i].density;
                maxDensitySpot = spots[i];
            }
        }

        const lat = Number(maxDensitySpot.latitude);
        const lng = Number(maxDensitySpot.longitude);

        if (!isNaN(lat) && !isNaN(lng)) {
            map.flyTo([lat, lng], 15, { duration: 1.5 });
        }
    }, [spots, map]);

    return null;
});

MapFocusEffect.displayName = "MapFocusEffect";

const OSM_TILE = {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
};

/**
 * SpotsMap — Reusable density map component for Dashboards.
 *
 * @param {Object} props
 * @param {Array}  props.spots  - Array of spot data objects
 * @param {Object} props.config - Configuration object describing field mappings:
 *
 *   config.popupFields: { title, local, bairro?, logradouro? }
 *   config.filters:     [{ key, label, type: "select"|"text", allLabel? }]
 *   config.details:     { title, bairro?, localType, period: { month, year } }
 *   config.stats:       [{ label, type: "count"|"unique"|"period", field?, monthField?, yearField? }]
 *   config.tileLayer?:  "carto" (default) | "osm"
 */
export function SpotsMap({ spots = [], config }) {
    const [selectedSpot, setSelectedSpot] = useState(null);
    const center = [-15.7801, -47.9292];

    // Build filter state dynamically from config
    const initialFilters = useMemo(() => {
        const state = {};
        config.filters.forEach((f) => {
            state[f.key] = "";
        });
        return state;
    }, [config.filters]);

    const [filters, setFilters] = useState(initialFilters);

    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    // Compute unique values for each select filter
    const filterOptions = useMemo(() => {
        const opts = {};
        const selectFilters = config.filters.filter(f => f.type === "select");
        if (selectFilters.length === 0) return opts;

        // Initialize Sets
        for (let i = 0; i < selectFilters.length; i++) {
            opts[selectFilters[i].key] = new Set();
        }

        // Single pass over spots
        for (let i = 0; i < spots.length; i++) {
            const spot = spots[i];
            for (let j = 0; j < selectFilters.length; j++) {
                const key = selectFilters[j].key;
                const val = spot[key];
                if (val != null) {
                    opts[key].add(val);
                }
            }
        }

        // Convert Sets to sorted arrays
        for (let i = 0; i < selectFilters.length; i++) {
            const key = selectFilters[i].key;
            opts[key] = Array.from(opts[key]).sort();
        }

        return opts;
    }, [spots, config.filters]);

    const [processedData, setProcessedData] = useState({ processedSpots: [], maxDensity: 1, statsData: [] });
    const [isFiltering, setIsFiltering] = useState(true);

    // Asynchronously filter spots, calculate density and statistics to avoid blocking the UI
    useEffect(() => {
        setIsFiltering(true);

        const timer = setTimeout(() => {
            if (!spots.length) {
                setProcessedData({ processedSpots: [], maxDensity: 1, statsData: [] });
                setIsFiltering(false);
                return;
            }

            // 1. Apply Filters
            const activeFilters = [];
            for (let i = 0; i < config.filters.length; i++) {
                const f = config.filters[i];
                const val = filters[f.key];
                if (val) {
                    activeFilters.push({
                        key: f.key,
                        value: val.toLowerCase()
                    });
                }
            }

            let filteredSpots = spots;
            if (activeFilters.length > 0) {
                filteredSpots = [];
                for (let i = 0; i < spots.length; i++) {
                    const spot = spots[i];
                    let match = true;
                    for (let j = 0; j < activeFilters.length; j++) {
                        const f = activeFilters[j];
                        const spotValue = spot[f.key];
                        if (!spotValue || !String(spotValue).toLowerCase().includes(f.value)) {
                            match = false;
                            break;
                        }
                    }
                    if (match) {
                        filteredSpots.push(spot);
                    }
                }
            }

            // 2. Calculate Density
            if (!filteredSpots.length) {
                // We still need to compute empty stats
                const emptyStats = config.stats.map(stat => ({
                    label: stat.label,
                    value: 0
                }));
                setProcessedData({ processedSpots: [], maxDensity: 1, statsData: emptyStats });
                setIsFiltering(false);
                return;
            }

            const radiusKm = 2;
            const latOffset = radiusKm / 111;
            const cellSize = latOffset;

            const grid = new Map();

            for (let i = 0; i < filteredSpots.length; i++) {
                const spot = filteredSpots[i];
                const lat = Number(spot.latitude);
                const lng = Number(spot.longitude);
                if (isNaN(lat) || isNaN(lng)) continue;

                const cellX = Math.floor(lng / cellSize);
                const cellY = Math.floor(lat / cellSize);
                const cellId = `${cellX}_${cellY}`;

                let cell = grid.get(cellId);
                if (!cell) {
                    cell = [];
                    grid.set(cellId, cell);
                }
                cell.push({ lat, lng });
            }

            let currentMaxDensity = 0;
            const spotsWithDensity = new Array(filteredSpots.length);
            
            for (let i = 0; i < filteredSpots.length; i++) {
                const spot = filteredSpots[i];
                const lat = Number(spot.latitude);
                const lng = Number(spot.longitude);

                if (isNaN(lat) || isNaN(lng)) {
                    spotsWithDensity[i] = { ...spot, density: 0 };
                    continue;
                }

                const cellX = Math.floor(lng / cellSize);
                const cellY = Math.floor(lat / cellSize);
                let density = 0;

                const lngOffset = radiusKm / (111 * Math.cos((lat * Math.PI) / 180));
                const latMin = lat - latOffset;
                const latMax = lat + latOffset;
                const lngMin = lng - lngOffset;
                const lngMax = lng + lngOffset;

                for (let dx = -1; dx <= 1; dx++) {
                    for (let dy = -1; dy <= 1; dy++) {
                        const neighborId = `${cellX + dx}_${cellY + dy}`;
                        const neighbors = grid.get(neighborId);
                        
                        if (neighbors) {
                            for (let j = 0; j < neighbors.length; j++) {
                                const n = neighbors[j];
                                if (
                                    n.lat >= latMin &&
                                    n.lat <= latMax &&
                                    n.lng >= lngMin &&
                                    n.lng <= lngMax
                                ) {
                                    if (n.lat === lat && n.lng === lng) {
                                        // Ignore exact duplicates of itself
                                    } else {
                                        density++;
                                    }
                                }
                            }
                        }
                    }
                }
                
                density = density + 1;

                if (density > currentMaxDensity) {
                    currentMaxDensity = density;
                }

                spotsWithDensity[i] = { ...spot, density };
            }

            // 3. Compute Stats
            const statResults = [];
            for (let i = 0; i < config.stats.length; i++) {
                const stat = config.stats[i];
                if (stat.type === "count") {
                    statResults.push({ ...stat, value: filteredSpots.length });
                } else if (stat.type === "unique" || stat.type === "period") {
                    statResults.push({ ...stat, set: new Set(), value: 0 });
                } else {
                    statResults.push({ ...stat });
                }
            }

            let needsIteration = false;
            for (let i = 0; i < statResults.length; i++) {
                if (statResults[i].set) {
                    needsIteration = true;
                    break;
                }
            }
            
            if (needsIteration) {
                for (let i = 0; i < filteredSpots.length; i++) {
                    const s = filteredSpots[i];
                    for (let j = 0; j < statResults.length; j++) {
                        const stat = statResults[j];
                        if (stat.type === "unique") {
                            stat.set.add(s[stat.field]);
                        } else if (stat.type === "period") {
                            stat.set.add(`${s[stat.monthField]}/${s[stat.yearField]}`);
                        }
                    }
                }

                for (let i = 0; i < statResults.length; i++) {
                    const stat = statResults[i];
                    if (stat.set) {
                        stat.value = stat.set.size;
                        delete stat.set;
                    }
                }
            }

            const finalStats = new Array(statResults.length);
            for (let i = 0; i < statResults.length; i++) {
                finalStats[i] = { label: statResults[i].label, value: statResults[i].value };
            }

            setProcessedData({ 
                processedSpots: spotsWithDensity, 
                maxDensity: Math.max(currentMaxDensity, 1),
                statsData: finalStats
            });
            setIsFiltering(false);

        }, 50);

        return () => clearTimeout(timer);
    }, [spots, filters, config.filters, config.stats]);

    const { processedSpots, maxDensity, statsData } = processedData;

    const [expandedCluster, setExpandedCluster] = useState(null);

    // Group spots with identical coordinates to render native exact-coordinate clusters
    const groupedSpots = useMemo(() => {
        const map = {};
        processedSpots.forEach(spot => {
            const lat = parseFloat(spot.latitude);
            const lng = parseFloat(spot.longitude);
            
            // Skip invalid or missing coordinates to prevent Leaflet crashes
            if (isNaN(lat) || isNaN(lng)) return;

            const key = `${lat}_${lng}`;
            if (!map[key]) {
                map[key] = {
                    id: key,
                    latitude: lat,
                    longitude: lng,
                    spots: [],
                    density: spot.density 
                };
            }
            map[key].spots.push(spot);
        });
        return Object.values(map);
    }, [processedSpots]);

    const handleSelectSpot = useCallback((spot) => {
        setSelectedSpot(spot);
    }, []);

    const handleExpandCluster = useCallback((id) => setExpandedCluster(id), []);
    const handleCloseCluster = useCallback(() => setExpandedCluster(null), []);

    const tile = OSM_TILE;

    function formatPeriod(period) {
        const monthNames = {
            1: "Janeiro",
            2: "Fevereiro",
            3: "Março",
            4: "Abril",
            5: "Maio",
            6: "Junho",
            7: "Julho",
            8: "Agosto",
            9: "Setembro",
            10: "Outubro",
            11: "Novembro",
            12: "Dezembro"
        };

        if (period?.year) {
            return `${monthNames[period.month]}/${period.year}`;
        }

        return `${monthNames[period.month]}`;
    }

    return (
        <Grid container spacing={2} sx={{ height: "100%", p: 2 }}>
            {/* Top Panel: Filters */}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
                        Filtros
                    </Typography>
                    <Grid container spacing={2}>
                        {config.filters.map((f, idx) => {
                            if (f.type === "text") {
                                return (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={f.key}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={f.label}
                                            placeholder={f.placeholder || ""}
                                            value={filters[f.key]}
                                            onChange={(e) => handleFilterChange(f.key, e.target.value)}
                                            variant="outlined"
                                        />
                                    </Grid>
                                );
                            }
                            // Default: select
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={f.key}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        select
                                        label={f.label}
                                        value={filters[f.key]}
                                        onChange={(e) => handleFilterChange(f.key, e.target.value)}
                                        variant="outlined"
                                        SelectProps={{
                                            native: true,
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <option value="">{f.allLabel || "Todas"}</option>
                                        {(filterOptions[f.key] || []).map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            </Grid>

            {/* Map Section */}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{ height: "550px", borderRadius: 2, overflow: "hidden", position: "relative" }}>
                    {isFiltering && (
                        <Box sx={{ 
                            position: "absolute", 
                            top: 0, left: 0, right: 0, bottom: 0, 
                            bgcolor: "rgba(255, 255, 255, 0.7)", 
                            zIndex: 1000, 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "center",
                            flexDirection: "column",
                            gap: 2
                        }}>
                            <CircularProgress color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                                Processando pontos...
                            </Typography>
                        </Box>
                    )}
                    <MapContainer
                        center={center}
                        zoom={4}
                        className="leaflet-container-dashboards"
                        style={{ height: "100%", width: "100%" }}
                        preferCanvas={true}
                    >
                        <TileLayer
                            attribution={tile.attribution}
                            url={tile.url}
                        />

                        <MapFocusEffect spots={processedSpots} />

                        {/* Highly Performant Memoized Group Rendering */}
                        {groupedSpots.map((group) => (
                            <MapGroup
                                key={group.id}
                                group={group}
                                isExpanded={expandedCluster === group.id}
                                onExpand={handleExpandCluster}
                                onClose={handleCloseCluster}
                                maxDensity={maxDensity}
                                totalSpots={processedSpots.length}
                                onSelect={handleSelectSpot}
                                config={config}
                            />
                        ))}
                    </MapContainer>
                </Paper>

                {/* Footer Section: Selected Spot Details & Statistics */}
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                        {/* Details Panel */}
                        <Grid item xs={12} md={7}>
                            {selectedSpot ? (
                                <Card 
                                    elevation={0} 
                                    sx={{ 
                                        borderRadius: 3, 
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.06)", 
                                        display: "flex", 
                                        flexDirection: "column", 
                                        height: "100%", 
                                        maxHeight: 450 
                                    }}
                                >
                                    <CardHeader
                                        title={
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                                                Detalhes do Local
                                            </Typography>
                                        }
                                        sx={{ pb: 1, borderBottom: "1px solid #f0f0f0", mb: 2 }}
                                    />
                                    <CardContent sx={{ pt: 0, flexGrow: 1, overflowY: "auto" }}>
                                        <Grid container spacing={2}>
                                            {/* Bloco: Natureza */}
                                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                                <Box sx={{ p: 1.5, bgcolor: "#f8f9fa", borderRadius: 2, height: "100%" }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", mb: 0.5 }}>
                                                        Natureza
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.primary" }}>
                                                        {selectedSpot[config.details.title]}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            {/* Bloco: Logradouro */}
                                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                                <Box sx={{ p: 1.5, bgcolor: "#f8f9fa", borderRadius: 2, height: "100%" }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", mb: 0.5 }}>
                                                        Logradouro
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
                                                        {selectedSpot.logradouro}, {selectedSpot.numero_logradouro}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            {/* Bloco: Bairro */}
                                            {config.details.bairro && (
                                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                                    <Box sx={{ p: 1.5, bgcolor: "#f8f9fa", borderRadius: 2, height: "100%" }}>
                                                        <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", mb: 0.5 }}>
                                                            Bairro
                                                        </Typography>
                                                        <Chip 
                                                            label={selectedSpot[config.details.bairro]} 
                                                            size="small" 
                                                            sx={{ fontWeight: 600, bgcolor: "rgba(0,0,0,0.06)", color: "text.primary" }} 
                                                        />
                                                    </Box>
                                                </Grid>
                                            )}

                                            {/* Bloco: Tipo de Local */}
                                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                                <Box sx={{ p: 1.5, bgcolor: "#f8f9fa", borderRadius: 2, height: "100%" }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", mb: 0.5 }}>
                                                        Tipo de Local
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
                                                        {selectedSpot[config.details.localType] || "Não informado"}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            {/* Bloco: Período */}
                                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                                <Box sx={{ p: 1.5, bgcolor: "#f8f9fa", borderRadius: 2, height: "100%" }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", mb: 0.5 }}>
                                                        Período
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
                                                        {formatPeriod({
                                                            month: selectedSpot[config.details.period.month],
                                                            year: selectedSpot[config.details.period.year]
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            {/* Bloco: Coordenadas */}
                                            <Grid item xs={12} sm={6} md={8} lg={6}>
                                                <Box sx={{ p: 1.5, bgcolor: "#f8f9fa", borderRadius: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", mb: 0.5 }}>
                                                        Coordenadas
                                                    </Typography>
                                                    <Box sx={{ display: "flex", gap: 2 }}>
                                                        <Typography variant="body2" sx={{ fontFamily: "'Fira Code', monospace", color: "text.secondary", bgcolor: "#fff", px: 1, py: 0.5, borderRadius: 1, border: "1px solid #e0e0e0" }}>
                                                            Lat: {selectedSpot.latitude}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: "'Fira Code', monospace", color: "text.secondary", bgcolor: "#fff", px: 1, py: 0.5, borderRadius: 1, border: "1px solid #e0e0e0" }}>
                                                            Lng: {selectedSpot.longitude}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>

                                            {/* Seção: Legenda de Densidade */}
                                            <Grid item xs={12}>
                                                <Box sx={{ pt: 2, mt: 1, borderTop: "1px dashed #e0e0e0" }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                                                        Legenda de Densidade
                                                    </Typography>
                                                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
                                                        {[
                                                            { label: "Baixa", color: "#90EE90" },
                                                            { label: "Média", color: "#FFD700" },
                                                            { label: "Alta", color: "#FF8C00" },
                                                            { label: "Muito Alta", color: "#DC143C" }
                                                        ].map((item) => (
                                                            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#f8f9fa", px: 1.5, py: 0.5, borderRadius: 5, border: "1px solid #f0f0f0" }}>
                                                                <Box sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: "50%", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }} />
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>{item.label}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ) : (
                                <Card 
                                    elevation={0} 
                                    sx={{ 
                                        borderRadius: 3, 
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.06)", 
                                        display: "flex", 
                                        flexDirection: "column", 
                                        height: "100%",
                                        minHeight: 300
                                    }}
                                >
                                    <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexGrow: 1, py: 4 }}>
                                        <Box sx={{ bgcolor: "#f8f9fa", p: 3, borderRadius: 4, textAlign: "center", mb: 4, width: "80%" }}>
                                            <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 500 }}>
                                                Selecione um local
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                Clique em um marcador no mapa para visualizar os detalhes detalhados.
                                            </Typography>
                                        </Box>

                                        <Box sx={{ width: "100%", pt: 3, borderTop: "1px dashed #e0e0e0", textAlign: "center" }}>
                                            <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                                                Legenda de Densidade
                                            </Typography>
                                            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1.5, justifyContent: "center" }}>
                                                {[
                                                    { label: "Baixa", color: "#90EE90" },
                                                    { label: "Média", color: "#FFD700" },
                                                    { label: "Alta", color: "#FF8C00" },
                                                    { label: "Muito Alta", color: "#DC143C" }
                                                ].map((item) => (
                                                    <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#f8f9fa", px: 1.5, py: 0.5, borderRadius: 5, border: "1px solid #f0f0f0" }}>
                                                        <Box sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: "50%", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>{item.label}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>

                        {/* Statistics Panel */}
                        <Grid item xs={12} md={5}>
                            <Card 
                                elevation={0} 
                                sx={{ 
                                    borderRadius: 3, 
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)", 
                                    display: "flex", 
                                    flexDirection: "column", 
                                    height: "100%", 
                                    maxHeight: 450 
                                }}
                            >
                                <CardHeader 
                                    title={
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                                            Estatísticas
                                        </Typography>
                                    } 
                                    sx={{ pb: 1, borderBottom: "1px solid #f0f0f0", mb: 2 }} 
                                />
                                <CardContent sx={{ pt: 0, flexGrow: 1, overflowY: "auto", px: 2 }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        {statsData.map((stat, index) => (
                                            <Box 
                                                key={stat.label} 
                                                sx={{ 
                                                    display: "flex", 
                                                    justifyContent: "space-between", 
                                                    alignItems: "center",
                                                    p: 1.5, 
                                                    borderRadius: 2,
                                                    bgcolor: index % 2 === 0 ? "#fafafa" : "transparent",
                                                    transition: "all 0.2s ease-in-out",
                                                    "&:hover": {
                                                        bgcolor: "#f0f4f8",
                                                        transform: "translateX(4px)"
                                                    }
                                                }}
                                            >
                                                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                                                    {stat.label}
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ fontWeight: "bold", bgcolor: "primary.50", px: 1.5, py: 0.5, borderRadius: 2 }}>
                                                    {stat.value}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    );
}
